import { CREATIVE_JOB_TYPES } from "./errors";

const collectPersistableImageKindUrls = (data) => {
  if (Array.isArray(data?.results)) {
    const fromFormats = data.results
      .flatMap((entry) =>
        (entry?.formats || [])
          .filter((formatResult) => formatResult?.ok && formatResult?.url)
          .map((formatResult) => formatResult.url)
      )
      .filter(Boolean);

    if (fromFormats.length > 0) {
      return fromFormats;
    }

    return data.results
      .filter((entry) => entry?.ok && entry?.url)
      .map((entry) => entry.url)
      .filter(Boolean);
  }

  if (Array.isArray(data?.images)) {
    return data.images
      .map((image) => (typeof image === "string" ? image : image?.url))
      .filter(Boolean);
  }

  return [];
};

const collectImageKindUrls = (data) => {
  if (Array.isArray(data?.results)) {
    const fromFormats = data.results
      .flatMap((entry) =>
        (entry?.formats || [])
          .filter((formatResult) => formatResult?.ok && formatResult?.url)
          .map((formatResult) => formatResult.url)
      )
      .filter(Boolean);

    if (fromFormats.length > 0) {
      return fromFormats;
    }

    return data.results
      .filter((entry) => entry?.ok && entry?.url)
      .map((entry) => entry.url)
      .filter(Boolean);
  }

  if (Array.isArray(data?.createdContents)) {
    return data.createdContents
      .flatMap((entry) => {
        if (Array.isArray(entry?.urls)) {
          return entry.urls;
        }
        return entry?.url ? [entry.url] : [];
      })
      .filter(Boolean);
  }

  if (Array.isArray(data?.images)) {
    return data.images
      .map((image) => (typeof image === "string" ? image : image?.url))
      .filter(Boolean);
  }

  return [];
};

const collectVideoKindUrls = (data) =>
  (data?.videos || [])
    .map((video) => (typeof video === "string" ? video : video?.url))
    .filter(Boolean);

/**
 * Extracts URLs from a stored creative-job response `{ ok, data: { kind } }`.
 * Also accepts the live resize HTTP body `{ data: urls[], results }`.
 */
export const extractCreativeJobResultUrls = ({ response } = {}) => {
  if (!response?.ok) return [];

  const data = response.data;

  if (data?.kind === "images") {
    return collectImageKindUrls(data);
  }

  if (data?.kind === "video") {
    return collectVideoKindUrls(data);
  }

  if (data?.kind === "url" && data.url) {
    return [data.url];
  }

  if (Array.isArray(data?.images)) {
    return collectImageKindUrls(data);
  }

  if (Array.isArray(data?.results)) {
    return collectImageKindUrls(data);
  }

  if (Array.isArray(data?.createdContents)) {
    return collectImageKindUrls(data);
  }

  if (Array.isArray(data)) {
    return data.filter(Boolean);
  }

  if (Array.isArray(response.results)) {
    return collectImageKindUrls({ results: response.results });
  }

  if (data?.url) {
    return [data.url];
  }

  return [];
};

/**
 * Extracts persistable URLs from a job response, excluding backend-created contents.
 */
export const extractCreativeJobPersistableUrls = ({ response } = {}) => {
  if (!response?.ok) return [];

  const data = response.data;

  if (data?.kind === "images") {
    return collectPersistableImageKindUrls(data);
  }

  if (data?.kind === "video") {
    return collectVideoKindUrls(data);
  }

  if (data?.kind === "url" && data.url) {
    return [data.url];
  }

  if (Array.isArray(data?.images)) {
    return collectPersistableImageKindUrls(data);
  }

  if (Array.isArray(data?.results)) {
    return collectPersistableImageKindUrls(data);
  }

  if (Array.isArray(data)) {
    return data.filter(Boolean);
  }

  if (Array.isArray(response.results)) {
    return collectPersistableImageKindUrls({ results: response.results });
  }

  if (data?.url) {
    return [data.url];
  }

  return [];
};

export const jobHasBackendCreatedContents = ({
  jobResponse,
  generationData,
} = {}) => {
  const responses = [jobResponse, generationData?.jobResponse].filter(Boolean);

  return responses.some((response) => {
    const data = response?.data;
    return (
      Array.isArray(data?.createdContents) && data.createdContents.length > 0
    );
  });
};

export const extractResizeResultUrls = (response) =>
  extractCreativeJobResultUrls({
    response,
    jobType: CREATIVE_JOB_TYPES.resizeImage,
  });

export const extractUrlKindResultUrls = (response) => {
  const url = response?.data?.url;
  return url ? [url] : [];
};

export const normalizeGeneratedMediaUrls = (generatedMedia = []) =>
  generatedMedia
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (entry?.url) return entry.url;
      if (Array.isArray(entry?.urls)) return entry.urls;
      return null;
    })
    .flat()
    .filter(Boolean);

const isPersistableResultUrl = (url) =>
  typeof url === "string" &&
  /^https?:\/\//i.test(url) &&
  !url.startsWith("creative-job-") &&
  !url.startsWith("job-");

const dedupeUrls = (urls = []) => [
  ...new Set(urls.filter(isPersistableResultUrl)),
];

export const resolveCreativeJobAlertResultUrls = ({
  generationData,
  jobResponse,
  resultUrl,
  resultUrls,
} = {}) => {
  const collected = [];

  if (Array.isArray(resultUrls)) {
    collected.push(...resultUrls);
  }

  if (resultUrl) {
    collected.push(resultUrl);
  }

  collected.push(
    ...normalizeGeneratedMediaUrls(generationData?.generatedMedia || [])
  );

  if (jobResponse) {
    collected.push(...extractCreativeJobResultUrls({ response: jobResponse }));
  }

  if (generationData?.jobResponse) {
    collected.push(
      ...extractCreativeJobResultUrls({
        response: generationData.jobResponse,
      })
    );
  }

  return dedupeUrls(collected);
};

export const resolveCreativeJobAlertPersistableUrls = ({
  generationData,
  jobResponse,
  resultUrl,
  resultUrls,
} = {}) => {
  const collected = [];

  if (Array.isArray(resultUrls)) {
    collected.push(...resultUrls);
  }

  if (resultUrl) {
    collected.push(resultUrl);
  }

  collected.push(
    ...normalizeGeneratedMediaUrls(generationData?.generatedMedia || [])
  );

  if (jobResponse) {
    collected.push(
      ...extractCreativeJobPersistableUrls({ response: jobResponse })
    );
  }

  if (generationData?.jobResponse) {
    collected.push(
      ...extractCreativeJobPersistableUrls({
        response: generationData.jobResponse,
      })
    );
  }

  return dedupeUrls(collected);
};
